import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import BottomBannerAd from './components/BottomBannerAd';
import Home from './pages/Home';
import YearHome from './pages/YearHome';
import SummerLeague from './pages/SummerLeague';
import Schedule from './pages/Schedule';
import YearSchedule from './pages/YearSchedule';
import Matches from './pages/Matches';
import MatchDetail from './pages/MatchDetail';
import Teams from './pages/Teams';
import TeamDetail from './pages/TeamDetail';
import PlayerDetail from './pages/PlayerDetail';
import Highlights from './pages/Highlights';
import Awards from './pages/Awards';
import AdminStats from './pages/AdminStats';


// Error Boundary Component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    console.error('ErrorBoundary caught error:', error);
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary componentDidCatch:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ 
          maxWidth: '1200px', 
          margin: '0 auto', 
          padding: '20px',
          textAlign: 'center',
          backgroundColor: '#e3f2fd',
          minHeight: '100vh'
        }}>
          <h1>Something went wrong.</h1>
          <details style={{ whiteSpace: 'pre-wrap', textAlign: 'left' }}>
            {this.state.error && this.state.error.toString()}
          </details>
        </div>
      );
    }

    return this.props.children;
  }
}

// Debug component to log current location
const DebugLocation = () => {
  const location = useLocation();
  console.log('Current location:', location.pathname, location.search, location.hash);
  console.log('Location state:', location.state);
  return null;
};

/** Bottom ad only on content pages AdSense should see. */
const shouldShowBottomAd = (pathname: string): boolean => {
  if (pathname === '/') return true;
  if (/^\/highlights\/[^/]+$/.test(pathname)) return true;
  if (/^\/summer-league\/matches\/[^/]+$/.test(pathname)) return true;
  return false;
};

const AppShell: React.FC = () => {
  const location = useLocation();
  const showBottomAd = shouldShowBottomAd(location.pathname);

  return (
    <>
      <DebugLocation />
      <div style={{ 
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#e3f2fd !important'
      }}>
        <Header />
        <Navigation />
        <main style={{ flex: 1, backgroundColor: '#e3f2fd !important' }}>
              <ErrorBoundary>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/summer-league/:year" element={<YearHome />} />
            <Route path="/summer-league/:year/schedule" element={<YearSchedule />} />
            <Route path="/summer-league/:year/:group" element={<SummerLeague />} />
            <Route path="/summer-league/:year/:group/schedule" element={<Schedule />} />
            <Route path="/summer-league/matches/:matchId" element={<MatchDetail />} />
            <Route path="/summer-league/:year/:group/teams" element={<Teams />} />
            <Route path="/summer-league/:year/:group/teams/:teamId" element={<TeamDetail />} />
            <Route path="/summer-league/:year/:group/awards" element={<Awards />} />
            <Route path="/players/:playerId" element={<PlayerDetail />} />
            <Route path="/highlights" element={<Highlights />} />
            <Route path="/highlights/:matchId" element={<Highlights />} />
            <Route path="/admin-stats" element={<AdminStats />} />
          </Routes>
              </ErrorBoundary>
        </main>
        {showBottomAd ? <BottomBannerAd /> : null}
        <Footer />
      </div>
    </>
  );
};

function App() {
  console.log('App component rendered');
  
  try {
  return (
    <Router>
        <ErrorBoundary>
      <AppShell />
        </ErrorBoundary>
    </Router>
  );
  } catch (error) {
    console.error('App component render error:', error);
    return (
      <div style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        padding: '20px',
        textAlign: 'center',
        backgroundColor: '#e3f2fd',
        minHeight: '100vh'
      }}>
        <h1>App failed to render</h1>
        <p>Check console for details</p>
      </div>
    );
  }
}

export default App;
